import { FC } from 'react';
import {
    Avatar,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Grid,
    Paper,
    Stack,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { blue } from '@mui/material/colors';
import { SCUIButton } from '../../../../atomic/atoms';
import { Circle, DragIndicator, SwapVert } from '@mui/icons-material';
import { StepPriorityServiceComponent } from '../../services';
import { Draggable } from 'react-beautiful-dnd';
import '../../CampaignManagement.scss';
import SCUIDraggableList from '../SCUIDraggableList/SCUIDraggableList';
import { CampaignStatuses } from '../../../../types/enums/campaign-enums';
import { SCUILiveCampaignFilter } from '../SCUILiveCampaignFilter/SCUILiveCampaignFilter';

export const SCUICampaignPriorityStep = ({ ...props }) => {
    const { t } = useTranslation();

    const {
        id: campaignId,
        stepperOnClickReset,
        saveCampaignForm,
        priorityList,
        onDragEndPriority,
        saveForm,
    } = StepPriorityServiceComponent({ ...props });

    const Item = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        backgroundColor: theme.palette.mode === 'dark' ? '#1A2027' : '#fff',
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    }));

    const headerItemSx = {
        border: '1px solid',
        display: 'flex',
        flexDirection: 'row',
        margin: '8px 0',
        padding: '8px',
        backgroundColor: 'whitesmoke',
    } as const;

    const itemSx = {
        border: '1px solid',
        display: 'flex',
        flexDirection: 'row',
        '&:hover': {
            boxShadow: '0px 0px 5px #0d6efd',
        },
        margin: '8px 0',
        padding: '8px',
    } as const;

    const currentItemSx = {
        backgroundColor: 'aliceblue',
        border: '1px solid #2962ff',
        display: 'flex',
        flexDirection: 'row',
        '&:hover': {
            boxShadow: '0px 0px 5px #0d6efd',
        },
        margin: '8px 0',
        padding: '8px',
    } as const;

    const DraggablePriorityItem: FC<any> = ({ priority, index }) => {
        let { id, title, beginDate, endDate, status } = priority;

        let order = index;
        const cardSx = campaignId == id ? currentItemSx : itemSx;
        return (
            <Draggable draggableId={id.toString()} index={index}>
                {(provided) => (
                    <div {...provided.draggableProps} ref={provided.innerRef}>
                        <Item sx={cardSx}>
                            <Typography
                                {...provided.dragHandleProps}
                                noWrap
                                style={{ display: 'flex', alignItems: 'center' }}
                            >
                                <DragIndicator sx={{ width: '24px', height: '24px' }} />
                            </Typography>

                            <Stack
                                spacing={2}
                                direction="row"
                                alignItems="center"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-around',
                                    width: '100%',
                                }}
                            >
                                <div style={{ width: '1%' }}>
                                    <Typography style={{ fontWeight: 'bold' }}>{order}</Typography>
                                </div>
                                <div style={{ width: '5%' }}>
                                    <Typography style={{ fontWeight: 'bold' }}>{id}</Typography>
                                </div>
                                <div style={{ width: '50%' }}>
                                    <Typography
                                        align="left"
                                        style={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}
                                    >
                                        <Tooltip
                                            title={t(CampaignStatuses.find((x: any) => x.id == status)?.name ?? '')}
                                            style={{ fontSize: '16px!important' }}
                                        >
                                            <Circle
                                                style={{
                                                    height: 16,
                                                    width: 16,
                                                    fontSize: '16px',
                                                    color: CampaignStatuses.find((x: any) => x.id == status)?.color,
                                                    marginRight: '8px',
                                                }}
                                            />
                                        </Tooltip>
                                        <Tooltip title={title}>
                                            {title.length > 75 ? `${title.substring(0, 75)}...` : title}
                                        </Tooltip>
                                    </Typography>
                                </div>
                                <div style={{ width: '15%' }}>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {new Intl.DateTimeFormat(navigator.language).format(new Date(beginDate))}
                                    </Typography>
                                </div>
                                <div style={{ width: '15%' }}>
                                    <Typography align="left" style={{ fontWeight: 'bold' }}>
                                        {new Intl.DateTimeFormat(navigator.language).format(new Date(endDate))}
                                    </Typography>
                                </div>
                            </Stack>
                        </Item>
                    </div>
                )}
            </Draggable>
        );
    };

    return (
        <Card className="step-form-card">
            <form
                id="StepPriority"
                name="StepPriority"
                noValidate={false}
                // onSubmit={stepperOnClickNext}
            >
                <CardHeader
                    className="step-form-card-header"
                    avatar={
                        <Avatar sx={{ bgcolor: blue[50], color: blue['A700'] }} variant="rounded">
                            <SwapVert sx={{ height: 24, width: 24 }} />
                        </Avatar>
                    }
                    title={t('campaignManagement.save.step.priority.title')}
                    subheader={t('campaignManagement.save.step.priority.description')}
                />

                <CardContent className="step-form-card-content">
                    <SCUILiveCampaignFilter />

                    <Grid container>
                        <Grid
                            size={{
                                xs: 12,
                                sm: 12,
                                md: 12,
                                lg: 12,
                            }}
                        >
                            <Item sx={headerItemSx}>
                                <Stack
                                    spacing={2}
                                    direction="row"
                                    alignItems="center"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-around',
                                        width: '100%',
                                        paddingLeft: '24px',
                                    }}
                                >
                                    <div style={{ width: '1%' }}>
                                        <Typography>
                                            {t('campaignManagement.save.step.priority.item.column.order')}
                                        </Typography>
                                    </div>
                                    <div style={{ width: '5%' }}>
                                        <Typography>
                                            {t('campaignManagement.save.step.priority.item.column.id')}
                                        </Typography>
                                    </div>
                                    <div style={{ width: '50%' }}>
                                        <Typography align="left">
                                            {t('campaignManagement.save.step.priority.item.column.campaignName')}
                                        </Typography>
                                    </div>
                                    <div style={{ width: '15%' }}>
                                        <Typography align="left">
                                            {t('campaignManagement.save.step.priority.item.column.beginDate')}
                                        </Typography>
                                    </div>
                                    <div style={{ width: '15%' }}>
                                        <Typography align="left">
                                            {t('campaignManagement.save.step.priority.item.column.endDate')}
                                        </Typography>
                                    </div>
                                </Stack>
                            </Item>
                            <SCUIDraggableList onDragEnd={onDragEndPriority}>
                                {priorityList &&
                                    priorityList.length > 0 &&
                                    priorityList.map((priority: any, index: number) => {
                                        return (
                                            <DraggablePriorityItem
                                                key={index}
                                                priority={priority}
                                                index={priority.rowNumber}
                                            />
                                        );
                                    })}
                            </SCUIDraggableList>
                        </Grid>
                    </Grid>
                </CardContent>
                <CardActions sx={{ m: 1 }} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Stack direction="row">
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.backButton')}
                                type="secondary"
                                sizeType="icon"
                                size={'s'}
                                buttonType="button"
                                onClick={stepperOnClickReset}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveButton')}
                                type="secondary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={() => saveCampaignForm({})}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                text={t('campaignManagement.save.step.action.saveAndContinueButton')}
                                type="primary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={saveForm}
                            />
                        </Item>
                    </Stack>
                </CardActions>
            </form>
        </Card>
    );
};
